/**
 * Ablauf der Variantenwahl als reine Funktionen (Zweitpruefung R1, B2, 01.11.2026): ohne React,
 * ohne window — offline mit tsx durchspielbar.
 *
 * Gemerkte andere Variante: zuerst ein sichtbarer Vorlauf, waehrenddessen ist „Andere Variante
 * waehlen" von Anfang an klickbar. Der Abbruch wird vor dem Start (ensure) UND vor jeder
 * Weiterleitung geprueft. Dauerhafter Rueckweg: `?variante=waehlen` zeigt den Picker auf jeder
 * Instanz des Kontos, ohne automatisches Oeffnen; die naechste Wahl ersetzt die gemerkte.
 */

export interface PlanVariante { port: number; shared?: boolean }

/** Sekunden zwischen Anzeige und automatischem Oeffnen der gemerkten Variante. */
export const VORLAUF_SEKUNDEN = 3;

/** `?variante=waehlen` (auch `wählen`/`wahl`) — der Picker wird erzwungen, nichts oeffnet sich selbst. */
export function waehlenErzwungen(search: string): boolean {
  const w = new URLSearchParams(search).get('variante');
  return w != null && ['waehlen', 'wählen', 'wahl'].includes(w.trim().toLowerCase());
}

/** Suchteil ohne `variante=…` — nach einer Wahl, damit ein Neuladen nicht wieder fragt. */
export function ohneWaehlenParameter(search: string): string {
  const p = new URLSearchParams(search);
  p.delete('variante');
  const s = p.toString();
  return s ? `?${s}` : '';
}

export type TorEntscheid =
  | { art: 'app' }                          // kein Picker: weiter in die App
  | { art: 'picker'; autoPort: number | null }; // Picker zeigen; autoPort = mit Vorlauf selbst oeffnen

/**
 * Was die Wurzel zeigt. `aufGeteiltemPort`: diese Instanz ist die gemeinsame Standard-Variante
 * (dort fragt der Picker; auf der eigenen Variante nur mit `?variante=waehlen`).
 */
export function torEntscheid(a: {
  varianten: readonly PlanVariante[];
  currentPort: number | null;
  gemerkterPort: number | null;
  erzwungen: boolean;
  schonGewaehlt: boolean;
}): TorEntscheid {
  if (a.varianten.length <= 1 || a.currentPort == null) return { art: 'app' };
  if (a.erzwungen) return { art: 'picker', autoPort: null };
  if (a.schonGewaehlt) return { art: 'app' };
  const aufGeteiltemPort = a.varianten.find(v => v.port === a.currentPort)?.shared === true;
  if (!aufGeteiltemPort) return { art: 'app' };
  if (a.gemerkterPort === a.currentPort) return { art: 'app' };
  const gemerkt = a.gemerkterPort == null ? undefined : a.varianten.find(v => v.port === a.gemerkterPort);
  return { art: 'picker', autoPort: gemerkt ? gemerkt.port : null };
}

/** Ein Schritt im Oeffnen einer Variante; `abgebrochen` wird an JEDER Stelle vorher gelesen. */
export type OeffnenSchritt =
  | { art: 'abbruch' }
  | { art: 'bleiben' }
  | { art: 'weiterleiten' }
  | { art: 'ensure' }
  | { art: 'warten' }
  | { art: 'fehler'; text: string };

/** Vor dem Start: bleiben (diese Instanz), Abbruch, sofort weiterleiten (geteilt) oder ensure. */
export function vorDemStart(v: PlanVariante, currentPort: number | null, abgebrochen: boolean): OeffnenSchritt {
  if (abgebrochen) return { art: 'abbruch' };
  if (v.port === currentPort) return { art: 'bleiben' };
  return v.shared ? { art: 'weiterleiten' } : { art: 'ensure' };
}

/** Nach einer ensure-Antwort: Abbruch zuerst, dann Fehler, laufend → weiterleiten, sonst warten. */
export function nachEnsure(r: { ok: boolean; status?: unknown; error?: unknown }, abgebrochen: boolean): OeffnenSchritt {
  if (abgebrochen) return { art: 'abbruch' };
  if (!r.ok) return { art: 'fehler', text: typeof r.error === 'string' && r.error ? r.error : 'Variante konnte nicht gestartet werden.' };
  return r.status === 'running' ? { art: 'weiterleiten' } : { art: 'warten' };
}
